import { useMemo } from 'react';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import type { PublicSpace } from '@dove/shared';
import { getSpaces, searchSpaces, setMembership } from '@/lib/api';
import { keys, queryClient } from '@/lib/query-client';

/**
 * Which rooms you are in.
 *
 * THIS FILE WAS THE SEAM, AND THIS IS THE MUTATION THAT REPLACED IT. It used to be a
 * `useSyncExternalStore` over a `Set` seeded from `mock.ts` and persisted to
 * `localStorage`, and its own comment said what would happen to it: "when there is a
 * server, this file is what a mutation and its cache invalidation replace — the
 * components do not change." They did not. `useJoinedSpaces`, `membershipIsYours` and
 * `toggleJoined` are the same three exports with the same three signatures, and
 * `JoinButton`, `SpaceResults` and the composer's destination picker are untouched.
 *
 * WHAT IT IS NOW: one react-query read of `GET /spaces` — the directory plus the ids
 * you are a member of — and one `PUT`/`DELETE` against
 * `/spaces/:slug/membership`. `localStorage` is gone with the store, and so is the
 * private-mode guard that went with it: the server holds the membership now, so a
 * reload cannot silently undo something you did, which is what the persistence was
 * for.
 *
 * THREE SURFACES STILL HAVE TO AGREE — search results mark a room you are in, the
 * room's screen draws Join or Joined, and the composer lists memberships and only
 * memberships (docs/5 § D260803-1933). One query key is what makes that true: they read the
 * same cache entry, so a join is visible on all three the moment it lands.
 */

/** The directory response, so the optimistic write below has a shape to edit. */
type Directory = Awaited<ReturnType<typeof getSpaces>>;

/**
 * Whether joining and leaving this room is yours to do.
 *
 * FALSE FOR TWO KINDS OF ROOM, for opposite reasons. **General** is where every
 * unaddressed letter goes, so leaving it is not a state the product has. **A sealed
 * room** is admitted to on the server — `id_verified` or two vouches, docs/2 § Spaces
 * — and a client that cannot grant admission must not draw a button that looks like
 * it can. Both render as the state you are in rather than as a disabled control:
 * there is nothing to press and therefore nothing to explain.
 *
 * IT KEYS OFF `kind` NOW RATHER THAN OFF A FIXTURE'S ID. It used to compare against
 * `MOCK_DEFAULT_SPACE.id`, which was only ever a stand-in for "this is the general
 * room" — the server says so on the room itself, and an id is per-database while the
 * kind is per-product.
 */
export function membershipIsYours(space: PublicSpace): boolean {
  return !space.sealed && space.kind !== 'general';
}

/**
 * `GET /spaces` — the whole list of rooms this account may see, plus which of them are
 * yours. Unpaginated and uncounted, because what reads it needs neither: this is where
 * `useJoinedSpaces` comes from, and the composer's destination picker lists your
 * memberships off it.
 *
 * IT IS NOT WHAT A LIST OF ROOMS ON SCREEN READS ANY MORE — that is `useSpaceRooms`
 * below (docs/5 § D260805-1500). The distinction is worth keeping straight: this
 * answer has no letter counts in it and is in no particular order, so a surface that
 * draws rooms as ROWS will be missing the figure the row is supposed to carry.
 */
export function useSpaceDirectory() {
  return useQuery({ queryKey: keys.spaces, queryFn: getSpaces });
}

/**
 * A PAGE OF ROOMS AS THEY ARE DRAWN — counted, ranked by how much has been written in
 * them, narrowed by `q` when there is one (docs/5 § D260805-1500).
 *
 * ONE HOOK, BECAUSE TWO SURFACES SHOW THE SAME LIST and react-query keys the cache by
 * this query's key: the search panel pages through it, and the Feed's empty state
 * takes the first page as its offer. A `useQuery` and a `useInfiniteQuery` under one
 * key store DIFFERENT shapes — `{ spaces, cursor }` against `{ pages, pageParams }` —
 * so the two surfaces sharing a cache entry is not a nicety here, it is the difference
 * between one fetch and a cache that answers with the wrong shape depending on which
 * screen mounted first. Both callers go through this, and neither spells out the
 * options.
 */
export function useSpaceRooms(q: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.spaceSearch(q),
    queryFn: ({ pageParam }) => searchSpaces(q, pageParam || undefined),
    initialPageParam: '',
    getNextPageParam: (last) => last.cursor,
    /*
     * READ WHEN THE PANEL OPENS, NOT ON EVERY FEED MOUNT. This is the most expensive
     * read in the product — the count on each row is an aggregate over open letters,
     * so an unnarrowed page groups the whole `post` table — and the Feed renders the
     * field that leads to it for everybody, most of whom never tap it. Same reasoning
     * as the GIF picker's `enabled: open`, for a heavier query.
     *
     * THE EMPTY FEED PASSES `true`, because its offer IS the first page and it only
     * renders when there is nothing else on the screen. Both callers share this
     * query's key, so whichever mounts first warms the other.
     */
    enabled,
    /* Rooms do not appear and disappear while somebody is typing, and backspacing
       through a query re-reads keys that are already in hand. */
    staleTime: 60_000,
    /* WHAT WAS FOUND STAYS ON SCREEN WHILE THE NEXT KEYSTROKE IS IN FLIGHT. Without
       this, every letter typed replaced the rows with skeletons and then with rows
       again — a list flashing under a thumb, which is the same complaint that made
       this search's order independent of the query (§ D260803-1933). The skeleton is
       for the first fetch only, when there is genuinely nothing on screen to keep. */
    placeholderData: keepPreviousData,
  });
}

export function useJoinedSpaces(): ReadonlySet<string> {
  const { data } = useSpaceDirectory();
  /* A new `Set` per response rather than per render: the three consumers compare it
     by identity in `useMemo` dependencies. */
  return useMemo(() => new Set(data?.joined ?? []), [data]);
}

/**
 * Join if you are out, leave if you are in.
 *
 * A NO-OP FOR A ROOM WHOSE MEMBERSHIP IS NOT YOURS, unchanged from the store: a
 * caller that forgets to check cannot invent an admission, and the server would refuse
 * it anyway.
 *
 * OPTIMISTIC, THEN INVALIDATED. The button has to answer the tap immediately — a Join
 * that waits for a round trip reads as a broken button, and this is the one control in
 * the product that is purely yours to press (I1). The write goes into the cached
 * directory so all three surfaces move together; a failure puts the old list back,
 * and either way the room, the directory and the feed are re-read afterwards, because
 * joining changes what the feed contains.
 */
export function toggleJoined(space: PublicSpace) {
  if (!membershipIsYours(space)) return;

  const before = queryClient.getQueryData<Directory>(keys.spaces);
  const joined = !before?.joined.includes(space.id);

  if (before) {
    queryClient.setQueryData<Directory>(keys.spaces, {
      ...before,
      joined: joined
        ? [...before.joined, space.id]
        : before.joined.filter((id) => id !== space.id),
    });
  }

  void setMembership(space.slug, joined)
    .catch(() => {
      /* Refused, or the network went. The list goes back to what the server last
         said, and the button follows it — no message, because the state on screen is
         the answer. */
      if (before) queryClient.setQueryData(keys.spaces, before);
    })
    .finally(() => {
      void queryClient.invalidateQueries({ queryKey: keys.spaces });
      void queryClient.invalidateQueries({ queryKey: keys.mySpaces });
      void queryClient.invalidateQueries({ queryKey: keys.space(space.slug) });
      /* A room you joined writes into your feed. */
      void queryClient.invalidateQueries({ queryKey: keys.feed });
    });
}
